import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

const cores = {
  navy: '#1A2233', border: '#d4dde8', muted: '#6b7a8d', orange: '#D85A30',
};
const inputStyle = { width: '100%', padding: '9px 12px', border: `1.5px solid ${cores.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, fontWeight: 700, color: cores.muted, display: 'block', marginBottom: 4 };
const btnPrimary = { padding: '10px 20px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' };
const btnGhost = { padding: '10px 20px', background: '#f0f3f7', color: cores.muted, border: `1px solid ${cores.border}`, borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' };

// Formata um valor numérico (string "1234.56") como moeda BR para exibição
const formatarMoeda = (valorNumStr) => {
  const num = parseFloat(valorNumStr || '0');
  if (isNaN(num)) return '';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Modal de NFS-e avulsa — sem orçamento vinculado. Cliente/tomador pode ser
// buscado na base de clientes cadastrados ou digitado na hora.
// Reaproveita POST /api/notas/avulsa no backend.
export default function NotaAvulsaModal({ onClose, toast, onSucesso }) {
  const [form, setForm] = useState({
    cliente_nome: '', cliente_cnpj: '', cliente_cpf: '',
    cliente_endereco: '', cliente_bairro: '', cliente_cidade: '', cliente_cep: '',
    cliente_email: '', codigo_servico: '', descricao: '', valor: '',
  });
  const [salvando, setSalvando] = useState(false);

  // Autocomplete de clientes cadastrados (mesmo padrão usado em Chat.js:
  // carrega a lista uma vez e filtra no cliente, contra /orcamentos/clientes/todos)
  const [clientes, setClientes] = useState([]);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const boxNomeRef = useRef(null);

  // Busca de CEP (ViaCEP via proxy backend /api/utils/cep/:cep)
  const [buscandoCep, setBuscandoCep] = useState(false);

  useEffect(() => {
    // Pré-preenche com o último código LC116 usado (se houver)
    api.get('/notas/lc116-padrao')
      .then(({ data }) => { if (data.codigo_servico) setForm(p => ({ ...p, codigo_servico: data.codigo_servico })); })
      .catch(() => {});

    api.get('/orcamentos/clientes/todos')
      .then(({ data }) => setClientes(data || []))
      .catch(() => {});
  }, []);

  // Fecha a lista de sugestões ao clicar fora do campo
  useEffect(() => {
    const aoClicarFora = (e) => {
      if (boxNomeRef.current && !boxNomeRef.current.contains(e.target)) setMostrarSugestoes(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, []);

  const set = (campo) => (e) => setForm(p => ({ ...p, [campo]: e.target.value }));

  const sugestoes = form.cliente_nome.trim().length >= 2
    ? clientes.filter(c =>
        (c.nome || '').toLowerCase().includes(form.cliente_nome.toLowerCase()) ||
        (c.cliente_apelido || '').toLowerCase().includes(form.cliente_nome.toLowerCase())
      ).slice(0, 8)
    : [];

  const selecionarCliente = (c) => {
    setForm(p => ({
      ...p,
      cliente_nome: c.nome || '',
      cliente_cnpj: c.cnpj || '',
      cliente_cpf: c.cpf || '',
      cliente_endereco: c.endereco || '',
      cliente_bairro: c.bairro || '',
      cliente_cidade: c.cidade || '',
      cliente_cep: c.cep || '',
      cliente_email: c.email || '',
    }));
    setMostrarSugestoes(false);
  };

  // Busca endereço pelo CEP quando o cliente não está cadastrado. Só
  // preenche campos que ainda estão vazios — não sobrescreve o que o
  // usuário já digitou (mesmo cuidado usado na busca por CNPJ do Orçamento).
  const buscarCep = async (cepDigitado) => {
    const cepLimpo = (cepDigitado || '').replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setBuscandoCep(true);
    try {
      const { data } = await api.get(`/utils/cep/${cepLimpo}`);
      if (data && !data.erro) {
        setForm(p => ({
          ...p,
          cliente_endereco: p.cliente_endereco || data.logradouro || '',
          cliente_bairro: p.cliente_bairro || data.bairro || '',
          cliente_cidade: p.cliente_cidade || (data.localidade ? `${data.localidade}-${data.uf}` : ''),
        }));
      } else {
        toast('CEP não encontrado.', 'error');
      }
    } catch {
      toast('Erro ao buscar CEP.', 'error');
    }
    setBuscandoCep(false);
  };

  const setCep = (e) => {
    const valor = e.target.value;
    setForm(p => ({ ...p, cliente_cep: valor }));
    const digitos = valor.replace(/\D/g, '');
    if (digitos.length === 8) buscarCep(digitos);
  };

  // Máscara de moeda: guarda o valor numérico real ("1234.56") no estado e
  // exibe formatado ("R$ 1.234,56") no input, igual ao padrão já usado em
  // outros formulários de valor do sistema.
  const setValorMask = (e) => {
    const digitos = e.target.value.replace(/\D/g, '');
    const num = (parseInt(digitos || '0', 10) / 100).toFixed(2);
    setForm(p => ({ ...p, valor: num }));
  };

  const salvar = async () => {
    if (!form.cliente_nome || (!form.cliente_cnpj && !form.cliente_cpf) || !form.codigo_servico || !form.valor) {
      toast('Preencha nome, CPF ou CNPJ, código do serviço (LC116) e valor.', 'error');
      return;
    }
    if (isNaN(parseFloat(form.valor)) || parseFloat(form.valor) <= 0) {
      toast('Valor inválido.', 'error');
      return;
    }
    setSalvando(true);
    try {
      await api.post('/notas/avulsa', form);
      toast('NFS-e avulsa emitida! Acompanhe o status na listagem.', 'success');
      onSucesso();
    } catch (e) {
      toast(e?.response?.data?.erro || 'Erro ao emitir nota avulsa.', 'error');
    }
    setSalvando(false);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>

        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${cores.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: cores.navy }}>🔧 Nova NFS-e Avulsa</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: cores.muted }}>✕</button>
        </div>

        <div style={{ padding: 24, overflowY: 'auto' }}>
          <div style={{ marginBottom: 14, position: 'relative' }} ref={boxNomeRef}>
            <label style={labelStyle}>NOME / RAZÃO SOCIAL DO TOMADOR *</label>
            <input
              value={form.cliente_nome}
              onChange={e => { set('cliente_nome')(e); setMostrarSugestoes(true); }}
              onFocus={() => setMostrarSugestoes(true)}
              style={inputStyle}
              placeholder="Nome de quem vai receber a nota"
              autoComplete="off"
            />
            {mostrarSugestoes && sugestoes.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                background: 'white', border: `1.5px solid ${cores.border}`, borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 20, maxHeight: 220, overflowY: 'auto',
              }}>
                {sugestoes.map(c => (
                  <div key={c.id} onClick={() => selecionarCliente(c)}
                    style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: `1px solid ${cores.border}`, fontSize: 13 }}
                    onMouseDown={e => e.preventDefault()}>
                    <div style={{ fontWeight: 600, color: cores.navy }}>{c.nome}{c.cliente_apelido ? ` (${c.cliente_apelido})` : ''}</div>
                    <div style={{ color: cores.muted, fontSize: 12 }}>{c.cnpj || c.cpf || 'Sem documento cadastrado'}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>
              Comece a digitar para buscar um cliente já cadastrado, ou preencha manualmente abaixo.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>CNPJ</label>
              <input value={form.cliente_cnpj} onChange={set('cliente_cnpj')} style={inputStyle} placeholder="Se pessoa jurídica" />
            </div>
            <div>
              <label style={labelStyle}>CPF</label>
              <input value={form.cliente_cpf} onChange={set('cliente_cpf')} style={inputStyle} placeholder="Se pessoa física" />
            </div>
          </div>
          <div style={{ fontSize: 12, color: cores.muted, marginTop: -8, marginBottom: 14 }}>Preencha apenas um dos dois — CNPJ ou CPF.</div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>ENDEREÇO</label>
            <input value={form.cliente_endereco} onChange={set('cliente_endereco')} style={inputStyle} placeholder="Rua, número" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>BAIRRO</label>
              <input value={form.cliente_bairro} onChange={set('cliente_bairro')} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>CEP {buscandoCep && <span style={{ fontWeight: 400 }}>⏳ buscando...</span>}</label>
              <input value={form.cliente_cep} onChange={setCep} style={inputStyle} placeholder="00000-000" maxLength={9} />
            </div>
          </div>
          <div style={{ fontSize: 11, color: cores.muted, marginTop: -8, marginBottom: 14 }}>
            Se o cliente não estiver cadastrado, digite o CEP para preencher endereço, bairro e cidade automaticamente.
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>CIDADE - UF</label>
            <input value={form.cliente_cidade} onChange={set('cliente_cidade')} style={inputStyle} placeholder="Ex: São Paulo-SP" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>E-MAIL (para envio da nota)</label>
            <input value={form.cliente_email} onChange={set('cliente_email')} style={inputStyle} type="email" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>CÓDIGO DO SERVIÇO (LC116) *</label>
              <input value={form.codigo_servico} onChange={set('codigo_servico')} style={inputStyle} placeholder="Ex: 1706" />
            </div>
            <div>
              <label style={labelStyle}>VALOR (R$) *</label>
              <input
                value={form.valor ? formatarMoeda(form.valor) : ''}
                onChange={setValorMask}
                style={inputStyle}
                inputMode="numeric"
                placeholder="R$ 0,00"
              />
            </div>
          </div>

          <div style={{ marginBottom: 4 }}>
            <label style={labelStyle}>DESCRIÇÃO DO SERVIÇO</label>
            <textarea value={form.descricao} onChange={set('descricao')} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="Ex: Prestação de serviço de intermediação/comissão" />
          </div>
        </div>

        <div style={{ padding: '14px 24px', borderTop: `1px solid ${cores.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnGhost} disabled={salvando}>Cancelar</button>
          <button onClick={salvar} style={btnPrimary} disabled={salvando}>{salvando ? 'Emitindo...' : 'Emitir NFS-e'}</button>
        </div>
      </div>
    </div>
  );
}
